import { AssumeRoleCommand, STSClient } from '@aws-sdk/client-sts';
import { type shared } from '@tig-dpqa-cloud/contract-backend-common';
import { InternalServerError } from 'http-errors';

export const getLogRoleSessionName = (userMail?: string, customRoleSessionName?: string) => {
  return `${userMail || customRoleSessionName || Date.now()}`;
};

export const getCloudwatchRumCredentialsFn = (cloudwatchRumRoleArn: string | undefined) => async () => {
  if (!cloudwatchRumRoleArn) {
    return;
  }

  const sts = new STSClient();

  const { Credentials } = await sts.send(
    new AssumeRoleCommand({
      RoleArn: cloudwatchRumRoleArn,
      RoleSessionName: `cloudwatch-rum-${Date.now()}`,
      DurationSeconds: 3600,
    }),
  );

  if (!Credentials) {
    throw new InternalServerError('Something went wrong while assuming the role');
  }

  return {
    accessKey: Credentials.AccessKeyId!,
    secretAccessKey: Credentials.SecretAccessKey!,
    sessionToken: Credentials.SessionToken!,
  };
};

export type GetCloudwatchRumCredentials = ReturnType<typeof getCloudwatchRumCredentialsFn>;

export const getBucketCredentialsFn = (
  bucketName: string,
  readSlidesBucketRoleArn: string,
  tenant: string,
  userMail?: string,
  roleSessionName?: string,
) => {
  const bucketArn = `arn:aws:s3:::${bucketName}`;

  return async (slideScanId?: string): Promise<shared.BucketCredentials> => {
    const sts = new STSClient();

    const resource = slideScanId ? `${bucketArn}/${tenant}/${slideScanId}/*` : `${bucketArn}/${tenant}/*`;

    const { Credentials } = await sts.send(
      new AssumeRoleCommand({
        RoleArn: readSlidesBucketRoleArn,
        RoleSessionName: getLogRoleSessionName(userMail, roleSessionName),
        DurationSeconds: 900,
        Policy: JSON.stringify({
          Version: '2012-10-17',
          Statement: {
            Action: ['s3:GetObject'],
            Effect: 'Allow',
            Resource: [resource],
          },
        }),
      }),
    );

    if (!Credentials) {
      throw new InternalServerError('Something went wrong while assuming the role');
    }

    return {
      accessKey: Credentials.AccessKeyId!,
      secretAccessKey: Credentials.SecretAccessKey!,
      sessionToken: Credentials.SessionToken!,
      bucketName,
    };
  };
};

export type GetBucketCredentials = ReturnType<typeof getBucketCredentialsFn>;

export const getBucketWithKeyCredentialsFn = ({
  bucketName,
  actions,
  roleToAssume,
  roleSessionName,
  userMail,
}: {
  bucketName: string;
  actions: string[];
  roleToAssume: string;
  roleSessionName: string;
  userMail?: string;
}) => {
  const bucketArn = `arn:aws:s3:::${bucketName}`;
  return async (key: string): Promise<shared.BucketCredentialsWithKey> => {
    const sts = new STSClient();

    const [objectKey, versionId] = key.split('#');

    if (!objectKey) {
      throw new Error('Invalid S3 object key');
    }

    const { Credentials } = await sts.send(
      new AssumeRoleCommand(
        {
          RoleArn: roleToAssume,
          RoleSessionName: getLogRoleSessionName(userMail, roleSessionName),
          DurationSeconds: 900,
          Policy: JSON.stringify({
            Version: '2012-10-17',
            Statement: {
              Action: actions,
              Effect: 'Allow',
              Resource: [`${bucketArn}/${objectKey}`],
              ...(versionId ? { Condition: { StringEquals: { 's3:versionid': versionId } } } : {}),
            },
          }),
        },
      ),
    );

    if (!Credentials) {
      throw new InternalServerError('Something went wrong while assuming the role');
    }

    return {
      accessKey: Credentials.AccessKeyId!,
      secretAccessKey: Credentials.SecretAccessKey!,
      sessionToken: Credentials.SessionToken!,
      bucketName,
      key,
    };
  };
};

export type GetBucketWithKeyCredentials = ReturnType<typeof getBucketWithKeyCredentialsFn>;

export const getBucketWithKeysCredentialsFn = ({
  bucketName,
  actions,
  roleToAssume,
  roleSessionName,
  userMail,
}: {
  bucketName: string;
  actions: string[];
  roleToAssume: string;
  roleSessionName: string;
  userMail?: string;
}) => {
  const bucketArn = `arn:aws:s3:::${bucketName}`;
  return async (keys: string[]): Promise<shared.BucketCredentialsWithKeys> => {
    const sts = new STSClient();
    const resources = keys.map((key) => `${bucketArn}/${key}`);

    const { Credentials } = await sts.send(
      new AssumeRoleCommand(
        {
          RoleArn: roleToAssume,
          RoleSessionName: getLogRoleSessionName(userMail, roleSessionName),
          DurationSeconds: 900,
          Policy: JSON.stringify({
            Version: '2012-10-17',
            Statement: {
              Action: actions,
              Effect: 'Allow',
              Resource: resources,
            },
          }),
        },
      ),
    );

    if (!Credentials) {
      throw new InternalServerError('Something went wrong while assuming the role');
    }

    return {
      accessKey: Credentials.AccessKeyId!,
      secretAccessKey: Credentials.SecretAccessKey!,
      sessionToken: Credentials.SessionToken!,
      bucketName,
      keys,
    };
  };
};

export type GetBucketWithKeysCredentials = ReturnType<typeof getBucketWithKeysCredentialsFn>;
